import { cn } from "@universe/ui/lib/utils";

import useBankAccountValues from "@/hooks/useBankAccountValues";
import { useGetCountryName } from "@/hooks/useGetCountryName";
import {
  AccountBankAccount,
  FilteredBeneficiaryDetail,
} from "@generated/types";

import CurrencyFlag from "./currency-flag";
import DetailRow from "./detail-row";

interface BeneficiaryBankDetailsProps {
  beneficiary: FilteredBeneficiaryDetail;
  singleColumn?: boolean;
  stack?: boolean;
  omitFields?: (keyof AccountBankAccount)[];
}

export default function BeneficiaryBankDetails({
  beneficiary,
  singleColumn = false,
  stack = false,
  omitFields = [],
}: BeneficiaryBankDetailsProps) {
  const getCountryName = useGetCountryName();

  const bankDetails = useBankAccountValues(
    beneficiary?.bankAccount as AccountBankAccount | undefined
  );

  // Filter out omitted fields
  const fieldsToDisplay = bankDetails.filter(
    (field) => !omitFields.includes(field.key as keyof AccountBankAccount)
  );

  if (fieldsToDisplay.length === 0) return null;

  return (
    <div
      className={cn(
        singleColumn ? "space-y-1" : "grid grid-cols-1 md:grid-cols-2 gap-2"
      )}
    >
      {fieldsToDisplay.map(({ key, label, value, isCountry }) => {
        if (isCountry) {
          const countryName = getCountryName(value);
          return (
            <DetailRow
              key={key}
              label={label}
              value={
                <div className="flex items-center gap-2">
                  <CurrencyFlag currency={value} size="20px" />
                  <span>{countryName}</span>
                </div>
              }
              stack={stack}
            />
          );
        }

        // Every non-country bank-detail value (account name, account
        // number, IBAN, BIC, sort code, etc.) is byte-exact and must not
        // be processed by browser translation extensions.
        return (
          <DetailRow
            key={key}
            label={label}
            value={value}
            stack={stack}
            translateValue={false}
          />
        );
      })}
    </div>
  );
}
